import type { ReactElement } from 'react';
import { LetterboxdUserResponse } from '.';
import { oscarDatabase } from '../../config/constants';

type BadgeDefinition = {
  key: string;
  label: string;
  getProgress: (args: {
    oscarFilmsStatus: LetterboxdUserResponse['oscarFilmsStatus'];
  }) => {
    watchedCount: number;
    totalCount: number;
    unwatchedFilmIds: string[];
  };
  renderBadgeArtwork: (args: { fill?: string; line?: string }) => ReactElement;
  completedLineColor: string;
  completedFillColor: string;
};

export const badges = {
  bestPicture: {
    key: 'bestPicture',
    label: 'Best Pictures',
    getProgress: ({ oscarFilmsStatus }) => {
      let bestPictureFilmIds = Object.values(
        oscarDatabase.nomineeCategories.bestPicture.nominees,
      ).map((item) => item.filmId);
      let bestPictureTmdbIds = bestPictureFilmIds
        .map(
          (filmId) =>
            oscarDatabase.films[filmId as keyof typeof oscarDatabase.films]
              ?.tmdb_data?.tmdb_id,
        )
        .map(String)
        .filter(Boolean);

      let watchedIds = bestPictureTmdbIds.filter((id) => {
        return oscarFilmsStatus[id]?.watched;
      });

      return {
        watchedCount: watchedIds.length,
        totalCount: bestPictureFilmIds.length,
        unwatchedFilmIds: bestPictureFilmIds.filter(
          (id) => !watchedIds.includes(id),
        ),
      };
    },
    completedFillColor: '#b6bfde',
    completedLineColor: '#ffffff',
    renderBadgeArtwork: ({ fill, line }) => (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 40.43 39.14"
        className="h-full w-full"
      >
        <g>
          <path
            d="M32.13,24.03c0,1.87-6.19,3.38-13.82,3.38s-13.82-1.51-13.82-3.38v-4.88c0-1.87,6.19-3.38,13.82-3.38s13.82,1.51,13.82,3.38v4.88Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <ellipse
            cx="18.34"
            cy="19.18"
            rx="13.82"
            ry="3.38"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <g>
            <path
              d="M12.82,17.98l2.81.74c.84.22.42.53-.94.69l-4.5.53c-.58.07-1.36.03-1.6-.07-1.41-.64-.4-1.39,2.55-1.9.5-.09,1.32-.08,1.68.02Z"
              fill={line}
            />
            <path
              d="M11.12,20.18l4.48-.52c1.36-.16,3.15-.11,3.99.11l2.74.72c.36.1.1.23-.55.28-3.8.28-8.08.16-10.88-.3-.48-.08-.37-.22.22-.29Z"
              fill={line}
            />
            <path
              d="M22,18.91l4.48-.52c.59-.07,1.37-.03,1.6.08,1.33.63.31,1.37-2.61,1.88-.5.09-1.31.08-1.67-.02l-2.74-.72c-.84-.22-.42-.53.94-.69Z"
              fill={line}
            />
            <path
              d="M25.6,18.14l-4.5.53c-1.36.16-3.15.11-3.99-.11l-2.81-.74c-.36-.1-.1-.23.55-.28,3.84-.28,8.18-.15,10.98.32.47.08.35.22-.23.29Z"
              fill={line}
            />
          </g>
        </g>
        <g>
          <path
            d="M28.64,16.84c0,1.87-6.19,3.38-13.82,3.38s-13.82-1.51-13.82-3.38v-4.88c0-1.87,6.19-3.38,13.82-3.38s13.82,1.51,13.82,3.38v4.88Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <ellipse
            cx="14.84"
            cy="11.98"
            rx="13.82"
            ry="3.38"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <g>
            <path
              d="M9.32,10.79l2.81.74c.84.22.42.53-.94.69l-4.5.53c-.58.07-1.36.03-1.6-.07-1.41-.64-.4-1.39,2.55-1.9.5-.09,1.32-.08,1.68.02Z"
              fill={line}
            />
            <path
              d="M7.62,12.99l4.48-.52c1.36-.16,3.15-.11,3.99.11l2.74.72c.36.1.1.23-.55.28-3.8.28-8.08.16-10.88-.3-.48-.08-.37-.22.22-.29Z"
              fill={line}
            />
            <path
              d="M18.51,11.72l4.48-.52c.59-.07,1.37-.03,1.6.08,1.33.63.31,1.37-2.61,1.88-.5.09-1.31.08-1.67-.02l-2.74-.72c-.84-.22-.42-.53.94-.69Z"
              fill={line}
            />
            <path
              d="M22.1,10.95l-4.5.53c-1.36.16-3.15.11-3.99-.11l-2.81-.74c-.36-.1-.1-.23.55-.28,3.84-.28,8.18-.15,10.98.32.47.08.35.22-.23.29Z"
              fill={line}
            />
          </g>
        </g>
        <g>
          <path
            d="M34.52,27.69c-1.64.9-5.93-3.8-9.6-10.5-3.67-6.69-5.31-12.85-3.67-13.74l4.28-2.35c1.64-.9,5.93,3.8,9.6,10.5,3.67,6.69,5.31,12.85,3.67,13.74l-4.28,2.35Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <ellipse
            cx="32.15"
            cy="13.26"
            rx="3.38"
            ry="13.82"
            transform="translate(-2.42 17.07) rotate(-28.7)"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <g>
            <path
              d="M30.55,7.85l.7,2.82c.21.84-.26.62-1.06-.5l-2.63-3.7c-.34-.48-.68-1.18-.7-1.44-.12-1.54,1.02-1.02,2.89,1.33.31.39.7,1.12.79,1.48Z"
              fill={line}
            />
            <path
              d="M27.81,7.41l2.61,3.68c.79,1.12,1.61,2.71,1.82,3.55l.68,2.75c.09.36-.16.2-.51-.35-2.07-3.2-4.02-7.01-4.96-9.69-.16-.46.02-.43.36.06Z"
              fill={line}
            />
            <path
              d="M34.15,16.35l2.61,3.68c.34.48.69,1.19.7,1.44.09,1.47-1.05.93-2.9-1.39-.31-.39-.7-1.12-.79-1.48l-.68-2.75c-.21-.84.26-.62,1.06.5Z"
              fill={line}
            />
            <path
              d="M36.55,19.13l-2.62-3.7c-.79-1.12-1.61-2.71-1.82-3.55l-.7-2.82c-.09-.36.16-.2.51.35,2.09,3.24,4.06,7.1,4.99,9.78.16.45-.02.42-.36-.06Z"
              fill={line}
            />
          </g>
        </g>
        <g>
          <path
            d="M16.46,27.42c1.44-.16,5.23,1.24,4.6,1.68v-4.85c.93-.93-3.17-1.84-4.6-1.68"
            fill="none"
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <path
            d="M13.42,33.68c1.45-.91,5.71-2.85,7.48-4.63v-4.38c0-.19-.21-.3-.37-.19-2.23,1.53-6.59,2.18-7.49,4.62v4.37c0,.19.21.31.37.21Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <path
            d="M23.92,35.06c-2.29-1.08-11.31-.07-10.75-1.58v-3.96c0-.24.23-.4.46-.32,1.84.66,8.39.11,10.3,1.01v4.85Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <path
            d="M24.45,35.12c.25.83-1.51,2.36-3.35,2.77v-5.07c1.46-.32,3.31-1.57,3.4-2.24s-.19,4.06-.04,4.54Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
        </g>
      </svg>
    ),
  },
  all: {
    key: 'all',
    label: 'All Nominees',
    getProgress: ({ oscarFilmsStatus }) => {
      let allFilmIds = Object.keys(oscarDatabase.films);
      let allTmdbIds = allFilmIds
        .map(
          (filmId) =>
            oscarDatabase.films[filmId as keyof typeof oscarDatabase.films]
              ?.tmdb_data?.tmdb_id,
        )
        .map(String)
        .filter(Boolean);

      let watchedIds = allTmdbIds.filter((id) => {
        return oscarFilmsStatus[id]?.watched;
      });

      return {
        watchedCount: watchedIds.length,
        totalCount: allFilmIds.length,
        unwatchedFilmIds: allFilmIds.filter((id) => !watchedIds.includes(id)),
      };
    },
    completedFillColor: '#f8ad11',
    completedLineColor: '#ffffff',
    renderBadgeArtwork: ({ fill, line }) => (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 35.35 36.18"
        className="h-full w-full"
      >
        <g>
          <path
            d="M25.62,29.3c0,.63,1.96,1.14,4.37,1.14s4.37-.51,4.37-1.14v-1.83h-8.73v1.83Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <ellipse
            cx="29.99"
            cy="26.69"
            rx="4.37"
            ry="1.14"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <path
            d="M26.73,22.81c0-.47,1.46-.85,3.26-.85s3.26.38,3.26.85v3.28c0,.47-1.46.85-3.26.85s-3.26-.38-3.26-.85v-3.28Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
          <ellipse
            cx="29.99"
            cy="22.76"
            rx="3.26"
            ry=".85"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
          <path
            d="M30.91,22.58c-.07-.81.4-3.33.44-4.19.03-.58-.06-1.23,0-1.8.04-.45.26-.97.3-1.44s.03-1.15,0-1.62-.28-.78.03-1.28c.12-.18.38-.34.5-.52.26-.38.36-1.32.42-1.8s.05-.95.11-1.42c-.09-.7-1.78-.94-2.03-1.35s.19-1.34.1-1.82c-.15-.75-1.42-.67-1.56-.06-.17.7.55,1.64-.18,2.09-.37.23-1.21.46-1.52.7-.53.43-.03,1.46-.12,2.03.15.53.07,1.12.39,1.58.19.27.56.44.62.82,0,.91-.14,1.9-.06,2.81.04.49.26,1.01.3,1.56.04.64-.03,1.33,0,1.98.04.83.44,3.73.44,3.73,0,0,.32.17.9.16s.92-.18.92-.18Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
        </g>
        <g>
          <path
            d="M1,29.3c0,.63,1.96,1.14,4.37,1.14s4.37-.51,4.37-1.14v-1.83H1v1.83Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <ellipse
            cx="5.37"
            cy="26.69"
            rx="4.37"
            ry="1.14"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <path
            d="M2.11,22.81c0-.47,1.46-.85,3.26-.85s3.26.38,3.26.85v3.28c0,.47-1.46.85-3.26.85s-3.26-.38-3.26-.85v-3.28Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
          <ellipse
            cx="5.37"
            cy="22.76"
            rx="3.26"
            ry=".85"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
          <path
            d="M6.29,22.58c-.07-.81.4-3.33.44-4.19.03-.58-.06-1.23,0-1.8.04-.45.26-.97.3-1.44s.03-1.15,0-1.62-.28-.78.03-1.28c.12-.18.38-.34.5-.52.26-.38.36-1.32.42-1.8s.05-.95.11-1.42c-.09-.7-1.78-.94-2.03-1.35s.19-1.34.1-1.82c-.15-.75-1.42-.67-1.56-.06-.17.7.55,1.64-.18,2.09-.37.23-1.21.46-1.52.7-.53.43-.03,1.46-.12,2.03.15.53.07,1.12.39,1.58.19.27.56.44.62.82,0,.91-.14,1.9-.06,2.81.04.49.26,1.01.3,1.56.04.64-.03,1.33,0,1.98.04.83.44,3.73.44,3.73,0,0,.32.17.9.16s.92-.18.92-.18Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
        </g>
        <g>
          <path
            d="M18.89,31.6c0,.74,2.31,1.34,5.15,1.34s5.15-.6,5.15-1.34v-2.16h-10.3v2.16Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <ellipse
            cx="24.04"
            cy="28.52"
            rx="5.15"
            ry="1.34"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <path
            d="M20.2,23.94c0-.55,1.72-1,3.84-1s3.84.45,3.84,1v3.87c0,.55-1.72,1-3.84,1s-3.84-.45-3.84-1v-3.87Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
          <ellipse
            cx="24.04"
            cy="23.89"
            rx="3.84"
            ry="1"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
          <path
            d="M25.12,23.68c-.08-.96.47-3.93.52-4.94.03-.68-.07-1.45,0-2.12.05-.53.31-1.14.35-1.69s.03-1.36,0-1.91-.33-.92.04-1.51c.14-.22.45-.4.59-.61.31-.44.43-1.55.49-2.12s.06-1.12.13-1.68c-.1-.83-2.11-1.11-2.39-1.59s.23-1.58.11-2.14c-.17-.88-1.67-.79-1.84-.07-.2.83.65,1.93-.22,2.47-.44.27-1.43.54-1.79.83-.63.51-.03,1.72-.14,2.4.18.62.08,1.32.46,1.87.22.32.66.51.73.96.01,1.08-.16,2.25-.07,3.32.05.58.31,1.19.35,1.83.05.76-.04,1.57,0,2.33.05.98.52,4.4.52,4.4,0,0,.38.2,1.06.19s1.09-.21,1.09-.21Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
        </g>
        <g>
          <path
            d="M6,31.6c0,.74,2.31,1.34,5.15,1.34s5.15-.6,5.15-1.34v-2.16H6v2.16Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <ellipse
            cx="11.16"
            cy="28.52"
            rx="5.15"
            ry="1.34"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <path
            d="M7.32,23.94c0-.55,1.72-1,3.84-1s3.84.45,3.84,1v3.87c0,.55-1.72,1-3.84,1s-3.84-.45-3.84-1v-3.87Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
          <ellipse
            cx="11.16"
            cy="23.89"
            rx="3.84"
            ry="1"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
          <path
            d="M12.24,23.68c-.08-.96.47-3.93.52-4.94.03-.68-.07-1.45,0-2.12.05-.53.31-1.14.35-1.69s.03-1.36,0-1.91-.33-.92.04-1.51c.14-.22.45-.4.59-.61.31-.44.43-1.55.49-2.12s.06-1.12.13-1.68c-.1-.83-2.11-1.11-2.39-1.59s.23-1.58.11-2.14c-.17-.88-1.67-.79-1.84-.07-.2.83.65,1.93-.22,2.47-.44.27-1.43.54-1.79.83-.63.51-.03,1.72-.14,2.4.18.62.08,1.32.46,1.87.22.32.66.51.73.96.01,1.08-.16,2.25-.07,3.32.05.58.31,1.19.35,1.83.05.76-.04,1.57,0,2.33.05.98.52,4.4.52,4.4,0,0,.38.2,1.06.19s1.09-.21,1.09-.21Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
        </g>
        <g>
          <path
            d="M11.8,33.64c0,.85,2.65,1.54,5.91,1.54s5.91-.69,5.91-1.54v-2.48h-11.83v2.48Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <ellipse
            cx="17.72"
            cy="30.1"
            rx="5.91"
            ry="1.54"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
            strokeWidth="2px"
          />
          <path
            d="M13.31,24.85c0-.63,1.97-1.15,4.41-1.15s4.41.51,4.41,1.15v4.44c0,.63-1.97,1.15-4.41,1.15s-4.41-.51-4.41-1.15v-4.44Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
          <ellipse
            cx="17.72"
            cy="24.79"
            rx="4.41"
            ry="1.15"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
          <path
            d="M18.96,24.54c-.1-1.1.54-4.51.59-5.67.04-.79-.08-1.67,0-2.44.06-.61.36-1.31.41-1.94s.04-1.56,0-2.2-.37-1.06.05-1.73c.16-.25.51-.46.68-.7.35-.51.49-1.78.57-2.43s.07-1.29.15-1.92c-.12-.95-2.42-1.27-2.74-1.82s.26-1.81.13-2.46c-.2-1.01-1.92-.91-2.12-.08-.23.95.75,2.22-.25,2.83-.5.31-1.64.62-2.05.95-.72.58-.04,1.97-.16,2.75.2.72.09,1.51.53,2.14.26.37.76.59.84,1.11.01,1.24-.19,2.58-.08,3.81.06.67.36,1.37.41,2.11.06.87-.04,1.8,0,2.68.05,1.13.6,5.05.6,5.05,0,0,.43.22,1.21.22s1.25-.24,1.25-.24Z"
            fill={fill}
            stroke={line}
            strokeMiterlimit="10"
          />
        </g>
      </svg>
    ),
  },
} satisfies Record<string, BadgeDefinition>;
